import axios from 'axios';
import {
  getRefreshToken,
  getAccessToken,
  setAccessToken,
  checkAndSaveRefreshTokenFromURL,
  checkAndSaveRedirectUrlFromURL,
  checkAndSaveCompanyFromURL,
  setUserInfo
} from '../utils/tokenManager';

// Backend URL from environment
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

// Create axios instance
const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple simultaneous token refresh requests
let isRefreshing = false;
let failedQueue = [];

// Flag to prevent multiple simultaneous auth initialization
let isInitializing = false;
let initPromise = null;

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

/**
 * Refresh access token using refresh token
 */
const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    console.log('Refreshing access token...');
    
    const response = await axios.get(`${BACKEND_URL}/api/auth/token`, {
      headers: {
        'Authorization': refreshToken
      }
    });

    const newAccessToken = response.data.message?.accessToken || response.data.accessToken;
    
    if (newAccessToken) {
      setAccessToken(newAccessToken);
      console.log('Access token refreshed successfully');
      return newAccessToken;
    } else {
      console.error('No access token in response:', response.data);
      throw new Error('No access token in response');
    }
  } catch (error) {
    console.error('Failed to refresh access token:', error);
    throw error;
  }
};

/**
 * Get user info from VIO API (verifies authentication)
 * User info is saved to localStorage by frontend
 */
const getUserInfo = async () => {
  try {
    console.log('Getting user info from VIO...');
    
    const response = await api.get('/users/info');
    
    if (response.data.status === 'Success' && response.data.message) {
      const userInfo = response.data.message;
      
      // Save to localStorage (always replace)
      setUserInfo(userInfo);
      
      console.log('User info retrieved successfully');
      return userInfo;
    } else {
      throw new Error('Invalid response from user info');
    }
  } catch (error) {
    console.error('Failed to get user info:', error);
    throw error;
  }
};

/**
 * Initialize tokens on app start
 * Check URL for refresh token, redirect URL, company, get access token, and sync user info
 * 
 * @returns {Object} { success: boolean, hasRefreshToken: boolean }
 */
export const initializeAuth = async () => {
  // Prevent duplicate initialization (React StrictMode causes double calls)
  if (isInitializing && initPromise) {
    console.log('⏳ Auth initialization already in progress, waiting...');
    return await initPromise;
  }
  
  isInitializing = true;
  initPromise = (async () => {
    try {
      // Check if refresh token is in URL and save it to localStorage
      const urlRefreshToken = checkAndSaveRefreshTokenFromURL();
      
      // Check if redirect URL is in URL and save it
      // This URL will be used for navigating to VIO voucher pages
      // Format: {redirectUrl}/viewRedeemedVoucher/{voucherNftId}
      checkAndSaveRedirectUrlFromURL();
      
      // Check if company is in URL and save it
      // Company info is provided by VIO miniapp URL instead of user/info API
      checkAndSaveCompanyFromURL();
      
      // Get refresh token (either from URL or localStorage)
      const refreshToken = urlRefreshToken || getRefreshToken();
      
      if (refreshToken) {
        // Get new access token
        await refreshAccessToken();
        
        // Get user info from VIO (verifies authentication)
        await getUserInfo();
        
        return { success: true, hasRefreshToken: true };
      } else {
        console.log('No refresh token found');
        return { success: false, hasRefreshToken: false };
      }
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      return { success: false, hasRefreshToken: !!getRefreshToken() };
    } finally {
      isInitializing = false;
      initPromise = null;
    }
  })();
  
  return await initPromise;
};

// Request interceptor - Add access token and refresh token to all requests
api.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();
    
    if (accessToken) {
      config.headers.Authorization = accessToken;
    }
    
    // Send refresh token via header (not URL) for backend authentication
    if (refreshToken) {
      config.headers['x-refresh-token'] = refreshToken;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle 401 errors and refresh token
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = token;
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newAccessToken = await refreshAccessToken();
        
        // Update the failed request with new token
        originalRequest.headers.Authorization = newAccessToken;
        
        // Process queued requests
        processQueue(null, newAccessToken);
        
        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        
        // If refresh fails, redirect to login or show error
        console.error('Token refresh failed, user needs to re-authenticate');
        
        // Optional: Clear tokens and redirect
        // clearTokens();
        // window.location.href = '/login';
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other errors
    if (error.response) {
      console.error('API Error:', error.response.status, error.response.data);
    } else if (error.request) {
      console.error('Network Error - Request made but no response received:', error.request);
      console.error('This might indicate CORS issue or server not responding');
    } else {
      console.error('Error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// ==========================================
// Marketing Pages API
// ==========================================

/**
 * Get all marketing pages
 */
export const getMarketingPages = async () => {
  const response = await api.get('/marketing-pages');
  return response.data;
};

/**
 * Get single marketing page by ID
 */
export const getMarketingPage = async (id) => {
  const response = await api.get(`/marketing-pages/${id}`);
  return response.data;
};

/**
 * Create new marketing page
 */
export const createMarketingPage = async (pageData) => {
  const response = await api.post('/marketing-pages', pageData);
  return response.data;
};

/**
 * Update marketing page
 */
export const updateMarketingPage = async (id, pageData) => {
  const response = await api.put(`/marketing-pages/${id}`, pageData);
  return response.data;
};

/**
 * Delete marketing page
 */
export const deleteMarketingPage = async (id) => {
  const response = await api.delete(`/marketing-pages/${id}`);
  return response.data;
};

/**
 * Toggle marketing page visibility (hide/unhide)
 */
export const toggleMarketingPageVisibility = async (id) => {
  const response = await api.patch(`/marketing-pages/${id}/toggle-visibility`);
  return response.data;
};

/**
 * Upload image for marketing page
 * @param {File} file - The image file to upload
 * @param {string} type - Image type: 'banner' | 'logo' | 'default'
 * @returns {Promise<{imageUrl: string, filename: string, original: Object, optimized: Object}>}
 */
export const uploadMarketingImage = async (file, type = 'default') => {
  const formData = new FormData();
  formData.append('image', file);
  
  const response = await api.post(`/marketing-pages/upload?type=${type}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * Upload base64 image for marketing page (converts to file first)
 * Stores original + generates optimized version for web display
 * 
 * @param {string} base64Data - Base64 data URL
 * @param {string} filename - Original filename
 * @param {string} type - Image type: 'banner' | 'logo' | 'default'
 * @returns {Promise<{imageUrl: string, filename: string, original: Object, optimized: Object}>}
 */
export const uploadBase64Image = async (base64Data, filename, type = 'default') => {
  // Convert base64 to blob
  const response = await fetch(base64Data);
  const blob = await response.blob();
  
  // Create file from blob
  const file = new File([blob], filename, { type: blob.type });
  
  return uploadMarketingImage(file, type);
};

/**
 * Delete uploaded image
 */
export const deleteMarketingImage = async (filename) => {
  const response = await api.delete(`/marketing-pages/upload/${filename}`);
  return response.data;
};

/**
 * Get full image URL from relative path
 */
export const getImageUrl = (relativePath) => {
  if (!relativePath) return null;
  if (relativePath.startsWith('http')) return relativePath;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';
  return `${backendUrl}${relativePath}`;
};

/**
 * Fetch published vouchers from VIO API
 */
export const fetchPublishedVouchers = async (page = 1, size = 100, tenantId, search = '') => {
  const response = await api.get('/campaign/voucherSchema/published/list', {
    params: { 
      page,
      size,
      ...(tenantId ? { tenantId } : {}),
      ...(search ? { search } : {})
    }
  });
  return response.data;
};

/**
 * Fetch online campaigns from VIO API
 * Returns campaigns where isActive === true AND endDatetime > today (or no endDatetime)
 */
export const fetchCampaigns = async (page = 1, size = 100) => {
  const response = await api.get('/campaign/list', {
    params: { 
      page,
      size
    }
  });
  return response.data;
};

/**
 * Fetch vouchers for a specific campaign
 */
export const fetchCampaignVouchers = async (campaignId, page = 1, size = 100000) => {
  const response = await api.get(`/campaign/${campaignId}/vouchers`, {
    params: { 
      page,
      size
    }
  });
  return response.data;
};

/**
 * VIO External API key (ops portal) — same contract as miniapp-claim-v2
 */
export const getVioExternalKeyStatus = async (options = {}) => {
  const params = {};
  if (options.verify) params.verify = 1;
  const res = await api.get('/vio-external-key/status', { params });
  return res.data;
};

export const saveVioExternalApiKey = async (apiKey) => {
  const res = await api.post('/vio-external-key', { apiKey });
  return res.data;
};

export default api;
